import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import { useSubmission } from '../../hooks/useSubmission';
import EditableMediaList from './EditableMediaList';
import MediaEntryList from './MediaEntryList';
import AnimatronicSelector from './AnimatronicSelector';

interface MediaEntry {
  id: string;
  url: string;
  type: 'image' | 'video';
  credit: string;
  credit_url: string;
}

interface FormData {
  media: MediaEntry[];
  original_animatronic_id?: string;
}

interface SubmissionFormProps {
  mode: 'new' | 'edit';
}

const initialFormData: FormData = {
  media: [{
    id: `entry-${Math.random().toString(36).substr(2, 9)}`,
    url: '',
    type: 'image',
    credit: '',
    credit_url: ''
  }]
};

const SubmissionForm: React.FC<SubmissionFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { submitAnimatronic, isLoading, error } = useSubmission();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [selectedAnimatronic, setSelectedAnimatronic] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (mode === 'edit') {
        await submitAnimatronic({
          ...selectedAnimatronic,
          media: formData.media.map(({ id, ...rest }) => rest),
          original_animatronic_id: selectedAnimatronic?.id
        }, mode);
      } else {
        await submitAnimatronic({
          ...formData,
          media: formData.media.map(({ id, ...rest }) => rest),
        }, mode);
      }
      
      navigate('/community/forum');
    } catch (err) {
      console.error('Submission error:', err);
    }
  };

  const handleChange = (field: keyof FormData, value: MediaEntry[] | string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAnimatronicSelect = (animatronic: any) => {
    setSelectedAnimatronic(animatronic);
    setFormData({
      media: animatronic.media?.map((m: any, index: number) => ({
        id: `existing-${index}`,
        url: m.url,
        type: m.type,
        credit: m.credit || '',
        credit_url: m.credit_url || ''
      })) || initialFormData.media,
      original_animatronic_id: animatronic.id
    });
  };

  if (mode === 'edit') {
    if (!selectedAnimatronic) {
      return (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Select Animatronic to Edit Media</h2>
          <AnimatronicSelector onSelect={handleAnimatronicSelect} />
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-8">
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
            {error.message}
          </div>
        )}

        {/* Display Selected Animatronic Info */}
        <div className="bg-background-secondary p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">{selectedAnimatronic.name}</h2>
          <div className="grid grid-cols-2 gap-4 text-content-secondary">
            <div>
              <span className="font-medium">Location:</span> {selectedAnimatronic.park}, {selectedAnimatronic.country}
            </div>
            <div>
              <span className="font-medium">Attraction:</span> {selectedAnimatronic.attraction}
            </div>
            <div>
              <span className="font-medium">Year:</span> {selectedAnimatronic.year}
            </div>
            <div>
              <span className="font-medium">Status:</span> {selectedAnimatronic.status}
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-4">Media Files</h2>
          <p className="text-content-secondary mb-4">
            Add, update, or reorder media files.
          </p>
          <EditableMediaList
            entries={formData.media}
            onChange={(media) => handleChange('media', media)}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center px-6 py-3 bg-brand hover:bg-brand-dark text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4 mr-2" />
            Submit Media Changes
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error.message}
        </div>
      )}

      <div>
        <h2 className="text-xl font-semibold mb-4">Media Files</h2>
        <EditableMediaList
          entries={formData.media}
          onChange={(media) => handleChange('media', media)}
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center px-6 py-3 bg-brand hover:bg-brand-dark text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <Send className="w-4 h-4 mr-2" />
          Submit for Review
        </button>
      </div>
    </form>
  );
};

export default SubmissionForm;
